import React, { useState } from 'react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    botcheck: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const contactChannels = [
    {
      type: 'email',
      title: 'Email Desk',
      value: 'info@rscindia.com',
      helper: 'For taxation, compliance and business advisory queries.',
      cta: 'Send Email',
      href: 'mailto:info@rscindia.com'
    },
    {
      type: 'phone',
      title: 'Call / WhatsApp',
      value: '+91 9819771203',
      helper: 'Mon-Sat · 10:00 AM to 7:00 PM',
      cta: 'Start Chat',
      href: 'https://wa.me/919819771203'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Share Your Requirement',
      text: 'Tell us your challenge — tax planning, GST, ITR, audit, NRI, or startup compliance.'
    },
    {
      step: '02',
      title: 'Quick Assessment Call',
      text: 'We identify immediate risk areas, deadlines, and the right advisory path for your case.'
    },
    {
      step: '03',
      title: 'Structured Action Plan',
      text: 'You receive a clear, milestone-driven plan with timelines and ownership for each task.'
    }
  ];

  const renderChannelIcon = (type) => {
    if (type === 'email') {
      return (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
          <path d="M4 7L12 13L20 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }

    if (type === 'phone') {
      return (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M7.2 4.5C7.5 4.2 7.92 4 8.37 4H10.3C10.94 4 11.49 4.45 11.63 5.08L12.16 7.45C12.28 7.98 12.11 8.52 11.72 8.89L10.43 10.13C11.16 11.66 12.38 12.9 13.88 13.66L15.11 12.37C15.49 11.98 16.03 11.82 16.55 11.94L18.92 12.47C19.55 12.61 20 13.16 20 13.8V15.63C20 16.08 19.82 16.5 19.5 16.8C18.77 17.52 17.75 17.86 16.74 17.71C9.84 16.66 7.34 14.16 6.29 7.26C6.14 6.25 6.48 5.23 7.2 4.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      );
    }

    if (type === 'linkedin') {
      return (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <rect x="4" y="9" width="4" height="11" stroke="currentColor" strokeWidth="1.8" />
          <path d="M14 20V13.8C14 11.7 15.7 10 17.8 10C19.9 10 21.6 11.7 21.6 13.8V20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="6" cy="5.8" r="1.8" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    }

    return (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect x="4" y="4" width="16" height="16" rx="5" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="12" cy="12" r="3.3" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="17.2" cy="6.9" r="1" fill="currentColor" />
      </svg>
    );
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formData.botcheck) {
      return;
    }

    const accessKey =
      (typeof process !== 'undefined' && process.env && process.env.REACT_APP_WEB3FORMS_KEY) ||
      '8fc4f221-966d-4853-a768-0b5ca4433625';

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: accessKey,
          subject: 'New Contact Inquiry - Ritesh CA',
          from_name: 'Ritesh CA Website',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          botcheck: formData.botcheck
        })
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Thanks! Your inquiry was sent successfully. We will get back to you shortly.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          botcheck: ''
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.message || 'Submission failed. Please try again in a moment.'
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Network error while submitting. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <section className="contact-hero-vailu" aria-label="Contact page hero">
        <div className="contact-hero-headline-wrap">
          <p className="contact-hero-eyebrow">Contact us</p>
          <h1 className="contact-hero-title">
            Let&apos;s simplify your<br />
            taxation and compliance
          </h1>
          <p className="contact-hero-subtitle">
            Reach out for direct CA guidance on tax, audit, and financial strategy.
            We respond quickly with clear next steps.
          </p>
        </div>

        <div className="contact-abstract-grid">
          <article className="contact-abs-card contact-abs-card--blue">
            <h3>Priority Consultation</h3>
            <p>Book a focused advisory call for urgent notices, filings, or high-value tax planning.</p>
            <a className="contact-abs-btn contact-abs-btn--outline" href="https://wa.me/919819771203" target="_blank" rel="noreferrer">
              WhatsApp Now
            </a>
          </article>

          <article className="contact-abs-card contact-abs-card--light">
            <div className="contact-hero-signal" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <p>Trusted by founders, SMEs, and NRI clients across India.</p>
          </article>

          <article className="contact-abs-card contact-abs-card--dark">
            <h3>Office Hours</h3>
            <p>Monday to Saturday · 10:00 AM – 7:00 PM (IST)</p>
          </article>
        </div>
      </section>

      <section className="contact-main-section">
        <div className="container">
          <div className="contact-main-grid">
            <div className="contact-channel-column">
              <div className="contact-section-head">
                <p className="contact-section-eyebrow">Ways to connect</p>
                <h2>Choose your preferred channel</h2>
              </div>

              <div className="contact-channel-grid">
                {contactChannels.map((channel) => (
                  <article className="contact-channel-card" key={channel.title}>
                    <span className="contact-channel-icon">{renderChannelIcon(channel.type)}</span>
                    <h3>{channel.title}</h3>
                    <p className="contact-channel-value">{channel.value}</p>
                    <p className="contact-channel-helper">{channel.helper}</p>
                    <a href={channel.href} target="_blank" rel="noreferrer">
                      {channel.cta}
                    </a>
                  </article>
                ))}
              </div>
            </div>

            <aside className="contact-form-column">
              <div className="contact-form-shell">
                <p className="contact-form-eyebrow">Quick inquiry</p>
                <h3>Tell us what you need</h3>
                <p className="contact-form-note">
                  Fill this snapshot and we&apos;ll get back with the right advisory route.
                </p>

                <form className="contact-form" onSubmit={handleSubmit}>
                  <input
                    type="checkbox"
                    name="botcheck"
                    className="contact-botcheck"
                    value={formData.botcheck}
                    onChange={handleInputChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />

                  <label>
                    Full Name
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      required
                    />
                  </label>
                  <label>
                    Email Address
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="you@example.com"
                      required
                    />
                  </label>
                  <label>
                    Phone Number
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91"
                      required
                    />
                  </label>
                  <label>
                    Message
                    <textarea
                      rows="4"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Share key details and timelines"
                      required
                    />
                  </label>
                  {submitStatus.message && (
                    <p className={`contact-form-status contact-form-status--${submitStatus.type}`}>
                      {submitStatus.message}
                    </p>
                  )}
                  <button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                  </button>
                </form>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="contact-process-section" aria-label="How engagement works">
        <div className="container">
          <div className="contact-section-head contact-section-head--center">
            <p className="contact-section-eyebrow">How we work</p>
            <h2>A structured consultation flow</h2>
          </div>

          <div className="contact-process-grid">
            {process.map((item) => (
              <article className="contact-process-card" key={item.step}>
                <span className="contact-process-step">{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="contact-findus-section" aria-label="Find us on map">
        <div className="container">
          <div className="contact-section-head contact-section-head--center">
            <p className="contact-section-eyebrow">Find us</p>
            <h2>Visit our office location</h2>
          </div>

          <div className="contact-map-shell">
            <iframe
              title="Ritesh CA Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3767.2408657703486!2d72.85721167519134!3d19.228331647172627!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b15a030d6131%3A0xf7ad0857258f661e!2sLaksha%20Labh%20CHSL%20(Carter%20Road)%20Borivali%20East!5e0!3m2!1sen!2sin!4v1777569491368!5m2!1sen!2sin"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;